import type { RunVisualArtifact, RunVisualsResponse, RunVisualViewId } from "@/api/run-visuals";
import { RUN_VISUAL_VIEW_ORDER, TORSION_VIEW_TYPES } from "@/api/run-visuals";
import { moleculeSubjects, resolveMoleculeSubject } from "@/components/runs/molecule-subjects";
import { isStructureBearingType } from "@/components/viz/molecule-structure";

/**
 * Which visualizations a run can offer *right now* — the progressive
 * selector behind the run view's visual panel (flowstack#950 Part B).
 *
 * Kept as pure functions over the visuals index so the three-state logic is
 * testable without mounting a chart, a 3D viewer, or a poll.
 */

/**
 * The three states a view can be in, and they are deliberately three.
 *
 * - `ready` — the data exists; render it.
 * - `pending` — it does not exist YET, and the run could still produce it.
 *   During a live run this is the *normal* state for most views, which is why
 *   it is offered-but-disabled rather than hidden: hiding it makes the panel
 *   look like it will never have plots, and showing it enabled makes an empty
 *   chart look like a bug.
 * - `absent` — the run is over and never produced it. A `.frcmod`, a log or a
 *   topology has nothing to draw and that is a legitimate outcome, not a
 *   failure.
 *
 * A FOURTH state — "produced but failed to load" — is deliberately NOT here:
 * it belongs to the fetch of a specific artifact, not to the index, and each
 * view surfaces it from its own query. Collapsing "not yet" into "failed" (or
 * either into "absent") is exactly the flowstack#923 defect, and during a live
 * run *most* things are legitimately not-yet.
 */
export type RunVisualAvailability = "ready" | "pending" | "absent";

export interface RunVisualView {
  id: RunVisualViewId;
  label: string;
  availability: RunVisualAvailability;
  /**
   * The artifact this view would render, when it has one. `null` for a view
   * that is not `ready`, and for the torsion view — whose data is fetched
   * per-fragment from the AFFDO routes rather than from a single artifact.
   */
  artifact: RunVisualArtifact | null;
  /** The node the molecule view is pinned to, or `null` for the run subject. */
  pinnedToNode?: string | null;
  /** How many structure inputs that node consumed; >1 for a fanned node. */
  siblingInputCount?: number;
  /**
   * The selected node this view FELL BACK FROM — set only by
   * {@link railMoleculeView}, and only when a step is selected, that step
   * consumed no structure, and the view is therefore the run's subject rather
   * than that step's input.
   *
   * It exists so the substitution can be SAID. A rail that quietly swapped the
   * run's ligand in under the heading `Input to merge_fragments` would make a
   * claim the data does not support, which is the objection
   * `deriveRunVisualViews` refuses the fallback over; carrying the node it fell
   * back from is what lets the heading distinguish the two states instead
   * (`describeMolecule` in `RunVisualBody.tsx`).
   */
  subjectFallbackFromNode?: string | null;
  /**
   * WHICH fragment of the run the molecule view is drawing, or `null` for the
   * run's whole ligand and for a structure whose fragment identity the index
   * could not establish (flowstack#1320).
   *
   * Read straight off the artifact rather than off the selection, so it names
   * what is ON SCREEN and not what was asked for — the two differ for one
   * frame after a pick, and permanently when a pick resolves to a fallback.
   */
  fragmentId?: string | null;
}

const VIEW_LABELS: Record<RunVisualViewId, string> = {
  molecule: "Molecule",
  torsion: "Torsion plots",
};

/**
 * Pick the artifact to show as "the molecule this run is about".
 *
 * Order of preference, and each step is a claim about meaning rather than a
 * tie-break:
 *
 * 1. The **picked fragment**, when the run cut fragments and one is picked
 *    (flowstack#1320). A fragmented run has several structures and the user
 *    chooses among them; the choice is the subject.
 * 2. Otherwise a structure-bearing artifact the run CONSUMED from outside
 *    itself — the ligand the user submitted. That is the run's subject, and it
 *    is available from the first moment the run exists, which is what lets the
 *    molecule stay visible for the *whole* live run rather than appearing once
 *    the first node finishes. It stays the DEFAULT: the first `Molecule` an
 *    AFFDO run produces is a fragment, so defaulting to one would be the
 *    fragment-labelled-as-ligand render inverted.
 * 3. Failing that, the first structure-bearing artifact the run produced.
 *    A standalone Tile run that takes no molecule input but emits one (a
 *    generator) still has a subject; so does a flow whose input row a
 *    retention sweep removed.
 *
 * Type-addressed at every step via `isStructureBearingType` — the viewer's
 * own registry, not a second copy of it, and never a tile or node name. The
 * order itself lives ONCE, in `molecule-subjects.ts`, because the picker has
 * to render the same list this resolves against; a second copy of "the run's
 * subject" is how a control ends up offering an entry the viewer will not show.
 */
export function pickSubjectArtifact(
  artifacts: readonly RunVisualArtifact[],
  selectedFragmentId: string | null = null
): RunVisualArtifact | null {
  return resolveMoleculeSubject(moleculeSubjects(artifacts), selectedFragmentId)?.artifact ?? null;
}

/**
 * The structure-bearing artifacts a given flow node CONSUMED, oldest first.
 *
 * "What did this step actually operate on" (flowstack#956) — which the
 * run-level subject cannot answer, because it is the same for every step. At
 * `fragment` this is the parent ligand; at `torsion_fit` it is a fragment.
 *
 * Type-addressed after filtering, never before: the node filter narrows WHICH
 * artifacts are candidates, and `isStructureBearingType` still decides which
 * of them can be drawn. No tile name reaches the decision.
 *
 * A FANNED node consumes a different artifact per child (one fragment each),
 * so this can legitimately return several. The caller shows the first and says
 * how many there are rather than silently picking one — an unlabelled "a
 * fragment" would be a claim the data does not support.
 */
export function nodeInputArtifacts(
  artifacts: readonly RunVisualArtifact[],
  nodeName: string
): RunVisualArtifact[] {
  return artifacts.filter(
    (a) => (a.consumed_by_nodes ?? []).includes(nodeName) && isStructureBearingType(a.typed_object)
  );
}

/** Whether any artifact in the index unlocks the torsion-plot view. */
export function hasTorsionData(artifacts: readonly RunVisualArtifact[]): boolean {
  return artifacts.some((a) => TORSION_VIEW_TYPES.has(a.typed_object));
}

/**
 * Build the selector's view list.
 *
 * `runIsLive` decides `pending` vs `absent` and nothing else. It comes from
 * the run view's existing progress signal (the DAG summary state), so a view
 * stops claiming "not produced yet" the moment the flow can no longer produce
 * anything — at which point "this run produced none" is the honest sentence.
 */
export function deriveRunVisualViews(args: {
  visuals: RunVisualsResponse | undefined;
  runIsLive: boolean;
  /**
   * The selected flow node, if any (flowstack#956). With one selected the
   * molecule view PINS to that step's input; with none it falls back to the
   * run's subject, so deselecting always restores the run-level view rather
   * than stranding the user on a step.
   */
  selectedNodeName?: string | null;
  /**
   * The run's fragment pick — the workspace's shared selection
   * (`stores/run-selection`), the same one the fit numbers and the torsion
   * plots read (flowstack#1320).
   *
   * `null` is "nothing picked", which resolves to the run's ligand here and to
   * the first fragment in the fits panel. Both are that surface's own default
   * for an unset pick, and both are what those surfaces already showed.
   */
  selectedFragmentId?: string | null;
}): RunVisualView[] {
  const { visuals, runIsLive, selectedNodeName = null, selectedFragmentId = null } = args;
  const artifacts = visuals?.artifacts ?? [];
  const nodeInputs =
    selectedNodeName === null ? [] : nodeInputArtifacts(artifacts, selectedNodeName);
  // A selected step with no renderable input is NOT a reason to silently
  // show the run subject instead: that would claim the step operated on the
  // ligand when it may have consumed a force field or a topology. Fall back
  // only when nothing is selected.
  const pinned = selectedNodeName !== null;
  // THE PIN FOLLOWS THE FRAGMENT WHERE IT CAN (flowstack#1320). A FANNED step
  // consumes one fragment per child, so "this step's input" is N structures
  // and the pin used to take the first unconditionally — which meant picking
  // `frag-1` and then clicking `torsion_fit` silently moved the view back to
  // `frag-0`. The fragment is the subject and the step is the stage, so the
  // pick survives the click; a step that did not consume the picked fragment
  // keeps its own first input, because a step's panel must answer "what did
  // THIS step operate on".
  const pinnedInput =
    (selectedFragmentId === null
      ? undefined
      : nodeInputs.find((a) => a.fragment_id === selectedFragmentId)) ??
    nodeInputs[0] ??
    null;
  const subject = pinned ? pinnedInput : pickSubjectArtifact(artifacts, selectedFragmentId);
  const torsion = hasTorsionData(artifacts);
  // An index that has not loaded yet is not evidence of absence. Treat it as
  // pending regardless of `runIsLive` so a terminal run does not flash "this
  // run produced no molecule" before its first response lands.
  const unloaded = visuals === undefined;
  const notReady: RunVisualAvailability = unloaded || runIsLive ? "pending" : "absent";

  return RUN_VISUAL_VIEW_ORDER.map((id) => {
    const ready = id === "molecule" ? subject !== null : torsion;
    return {
      id,
      label: VIEW_LABELS[id],
      availability: ready ? "ready" : notReady,
      artifact: id === "molecule" ? subject : null,
      pinnedToNode: id === "molecule" && pinned ? selectedNodeName : null,
      siblingInputCount: id === "molecule" && pinned ? nodeInputs.length : 0,
      // From the ARTIFACT, never from `selectedFragmentId`: a pick that
      // resolved to a fallback must not name the fragment it failed to find.
      fragmentId: id === "molecule" ? (subject?.fragment_id ?? null) : null,
    };
  });
}

/**
 * The molecule view's availability for the RUN AS A WHOLE — unpinned.
 *
 * The rail asks a different question from the viewer (flowstack#1221 finding
 * 1). The viewer asks *"what should I draw right now"*, which a node selection
 * legitimately re-points at that step's input. The rail asks *"is there a
 * structure section on this screen at all"*, and that must not depend on the
 * selection: a step that consumed a force field and no structure is a reason
 * for the section to say what it is showing, never a reason for the section to
 * vanish out from under the user mid-click. Losing the structure on a step
 * click is the exact behaviour VIS/V6 was written to abolish.
 *
 * So it re-derives with `selectedNodeName: null` rather than restating the
 * three-state rule: one rule, asked twice with different arguments.
 */
export function runMoleculeAvailability(args: {
  visuals: RunVisualsResponse | undefined;
  runIsLive: boolean;
}): RunVisualAvailability {
  return moleculeViewOf(deriveRunVisualViews({ ...args, selectedNodeName: null })).availability;
}

/**
 * `RUN_VISUAL_VIEW_ORDER` always carries `molecule`, so this stand-in is
 * unreachable — and it is `pending` rather than `absent` if that ever changes,
 * since "the index does not list it" is not evidence the run produced none.
 */
const NO_MOLECULE_VIEW: RunVisualView = {
  id: "molecule",
  label: VIEW_LABELS.molecule,
  availability: "pending",
  artifact: null,
  pinnedToNode: null,
  siblingInputCount: 0,
  fragmentId: null,
};

function moleculeViewOf(views: readonly RunVisualView[]): RunVisualView {
  return views.find((v) => v.id === "molecule") ?? NO_MOLECULE_VIEW;
}

/**
 * The molecule view for the RAIL — the third asking of the same rule.
 *
 * The rail asks *"is there a structure on this screen at all"*, and
 * `runMoleculeAvailability` already answers that for the SECTION. This answers
 * it for the section's CONTENT, which was the half of flowstack#1221 finding 1
 * that stayed broken (flowstack#1237): the section survived a step click while
 * the body it wrapped went to "this step has no molecule input", so the
 * structure still left the screen on every step that consumed a force field, a
 * topology or nothing indexed as structure-bearing. A section that keeps its
 * border and loses its molecule is the behaviour VIS/V6 was written to abolish,
 * arrived at one layer in.
 *
 * The rule, in order:
 *
 * 1. **The pin wins where it means something.** A step that DID consume a
 *    structure keeps drawing that input and keeps saying `Input to <node>` —
 *    flowstack#956, untouched.
 * 2. **Otherwise the run's subject, said out loud.** The fallback carries
 *    `subjectFallbackFromNode`, so the heading states it is the run's molecule
 *    rather than the step's. A SILENT substitution is what
 *    `deriveRunVisualViews` refuses, and it is right to: in the molecule MODE
 *    the panel is the answer to "what did this step operate on", and showing
 *    the ligand there would answer it falsely. The rail is a different
 *    question, which is why the fallback belongs here and not there.
 * 3. **Neither renderable → the run-level view**, so the body's sentence and
 *    the section gate (`runMoleculeAvailability`, same unpinned derivation)
 *    cannot disagree about whether there is a structure at all.
 *
 * Derived by calling `deriveRunVisualViews` twice with different arguments
 * rather than by changing its contract — one rule, asked twice, exactly as the
 * availability above.
 */
export function railMoleculeView(args: {
  visuals: RunVisualsResponse | undefined;
  runIsLive: boolean;
  selectedNodeName?: string | null;
  /** Threaded through both derivations, so the rail's structure follows the
   * run's fragment pick on BOTH paths — the pin and the subject fallback.
   * A fallback that reverted to `frag-0` would move the molecule under the
   * user on a click that changed no selection they made (flowstack#1320). */
  selectedFragmentId?: string | null;
}): RunVisualView {
  const { selectedNodeName = null } = args;
  const pinned = moleculeViewOf(deriveRunVisualViews(args));
  if (selectedNodeName === null || pinned.availability === "ready") return pinned;
  const subject = moleculeViewOf(deriveRunVisualViews({ ...args, selectedNodeName: null }));
  if (subject.availability !== "ready") return subject;
  return { ...subject, subjectFallbackFromNode: selectedNodeName };
}

/**
 * The view to show, given the user's pick.
 *
 * Falls back to the first `ready` view — and finally to the first view at all
 * — so a selection can never strand the panel on a view whose data has gone
 * (a regenerate, or a resume that superseded an artifact). Mirrors the
 * Analysis tab's `selectionIsCurrent` reconciliation for the same reason.
 */
export function resolveSelectedView(
  views: readonly RunVisualView[],
  selected: RunVisualViewId | null
): RunVisualView {
  const picked = views.find((v) => v.id === selected && v.availability === "ready");
  return picked ?? views.find((v) => v.availability === "ready") ?? views[0];
}

/**
 * A stable key over the run's PROGRESS, for `useRunVisuals`.
 *
 * This is what makes the panel progressive without a second polling loop: the
 * DAG query already re-reads every 5s while the run is active, and every one
 * of its responses carries each node's status. Folding those into a key means
 * the visuals index re-reads exactly when a node moved and never otherwise —
 * so a quiet flow, and a terminal one (whose DAG poll has stopped), cost
 * nothing.
 *
 * Node NAME is included, not just the status: a node appearing at all (a
 * fanout expanding) is progress the status list alone would miss when the new
 * node's status happens to match an existing one.
 *
 * `updated_at` is included for the case name/status/child_count cannot see
 * (Rupert PR-955 r0): while a `torsion_fit` FANOUT drains, the aggregate node
 * stays `running` with a stable `child_count` as its children complete one by
 * one — so a key without it would go unchanged for the entire stretch during
 * which the torsion plots are actually arriving, which is the whole point of
 * the surface. The backend aggregates the newest child transition into the
 * node's `updated_at`, so it moves on exactly those completions.
 */
export function runProgressKey(
  nodes:
    | readonly {
        name: string;
        status: string | null;
        child_count?: number;
        updated_at?: string | null;
      }[]
    | undefined
): string {
  if (nodes === undefined) return "unloaded";
  return nodes
    .map((n) => `${n.name}:${n.status ?? "-"}:${n.child_count ?? 0}:${n.updated_at ?? "-"}`)
    .join("|");
}
